import { Injectable, OnDestroy } from '@angular/core';
import { 
  CapacitorSQLite, 
  SQLiteConnection, 
  SQLiteDBConnection, 
  capSQLiteResult, 
  capSQLiteValues 
} from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, Subject } from 'rxjs';

// Interfaces for type safety
interface FinishTime {
  id?: number;
  time: number;
}

interface Barcode {
  id?: number;
  user: number;
  position: number;
}

interface DatabaseResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

// Constants
const DATABASE_CONFIG = {
  DB_NAME: 'cmcDB',
  DB_VERSION: 1,
  ENCRYPTION: 'no-encryption',
  READ_ONLY: false,
  TABLES: {
    FINISH_TIMES: 'finish_times',
    BARCODES: 'barcodes'
  }
} as const;

@Injectable({
  providedIn: 'root'
})
export class DatabaseService implements OnDestroy {
  private readonly sqlite: SQLiteConnection;
  private db: SQLiteDBConnection | null = null;
  private readonly destroy$ = new Subject<void>();

  // Public state observables
  public readonly isInitialized$ = new BehaviorSubject<boolean>(false);
  public readonly connectionStatus$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected'>('disconnected');

  // Configuration
  public readonly config = {
    dbName: DATABASE_CONFIG.DB_NAME,
    isNative: Capacitor.isNativePlatform(),
    version: DATABASE_CONFIG.DB_VERSION
  };

  // Legacy properties for backward compatibility
  initialised = false;
  dbName = DATABASE_CONFIG.DB_NAME;
  isNative = Capacitor.isNativePlatform();

  // Preferences key for deferred clear. Set before any DELETE attempt so
  // the intent survives app kills; cleared only after a confirmed delete.
  private static readonly CLEAR_TIMES_PENDING_KEY = 'cmc_clear_times_pending';

  constructor() {
    console.log('🗄️ DatabaseService initializing...');
    this.sqlite = new SQLiteConnection(CapacitorSQLite);
  }

  /**
   * Initialize the Web store for web platform
   */
  public async initWebStore(): Promise<DatabaseResult> {
    try {
      if (this.config.isNative) {
        return {
          success: false,
          error: 'Web store initialization not required for native platforms'
        };
      }

      if (!this.sqlite) {
        return {
          success: false,
          error: 'SQLite connection not available'
        };
      }

      await this.sqlite.initWebStore();
      //console.log('✅ Web store initialized successfully');
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to initialize web store: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Initialize the database connection and create tables
   */
  public async initializeDatabase(): Promise<DatabaseResult> {
    try {
      this.connectionStatus$.next('connecting');
      //console.log('🔄 Initializing database...');

      // Initialize web store for web platform FIRST
      if (!this.config.isNative) {
        const webStoreResult = await this.initWebStore();
        if (!webStoreResult.success) {
          throw new Error(webStoreResult.error);
        }
      }

      // Check connection consistency
      const consistencyResult = await this.sqlite.checkConnectionsConsistency();
      //console.log('📊 Connection consistency check:', consistencyResult.result);

      // Check if connection already exists
      const isConnectionResult = await this.sqlite.isConnection(
        this.config.dbName, 
        DATABASE_CONFIG.READ_ONLY
      );

      if (!isConnectionResult.result) {
        // Create a new connection
       // console.log('🔧 Creating new database connection...');
        this.db = await this.sqlite.createConnection(
          this.config.dbName,
          DATABASE_CONFIG.READ_ONLY,
          DATABASE_CONFIG.ENCRYPTION,
          this.config.version,
          false
        );
      } else {
        // Retrieve existing connection
        console.log('🔄 Retrieving existing database connection...');
        this.db = await this.sqlite.retrieveConnection(
          this.config.dbName,
          DATABASE_CONFIG.READ_ONLY
        );
      }

      if (!this.db) {
        throw new Error('Failed to establish database connection');
      }

      // CRITICAL: Always check if database is open before opening
      const isDbOpen = await this.db.isDBOpen();
    //  console.log('📋 Database open status:', isDbOpen.result);

      if (!isDbOpen.result) {
        // Open the database
       // console.log('🔓 Opening database...');
        await this.db.open();
      //  console.log('✅ Database connection opened');
      } else {
        console.log('✅ Database already open');
      }

      // Verify the database is actually open before creating tables
      const verifyOpen = await this.db.isDBOpen();
      if (!verifyOpen.result) {
        throw new Error('Database failed to open properly');
      }

      // Create tables
      await this.createTables();

      // If a clearTimes() was interrupted by a previous app kill or
      // DB-close, the pending flag will still be set.  Execute the DELETE
      // now while we have a guaranteed-open connection.
      await this.executePendingClear();

      // Update state
      this.initialised = true;
      this.isInitialized$.next(true);
      this.connectionStatus$.next('connected');

      //console.log('✅ Database initialized successfully');
      return { success: true };

    } catch (error) {
      const errorMessage = `Database initialization failed: ${error}`;
      console.error('❌', errorMessage);
      
      // Clean up on error
      if (this.db) {
        try {
          await this.db.close();
        } catch (closeError) {
          console.error('Error closing database after failure:', closeError);
        }
        this.db = null;
      }
      
      this.initialised = false;
      this.isInitialized$.next(false);
      this.connectionStatus$.next('disconnected');
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Legacy method name for backward compatibility
  async initialiseDatabase(): Promise<DatabaseResult> {
    return await this.initializeDatabase();
  }

  /**
   * Create all required database tables
   */
  private async createTables(): Promise<void> {
    if (!this.db) {
      throw new Error('Database connection not available');
    }

    // Double-check database is open
    const isOpen = await this.db.isDBOpen();
    if (!isOpen.result) {
      throw new Error('Database is not open for table creation');
    }

   // console.log('📋 Creating database tables...');

    try {
      // Create finish_times table
      const finishTimesSchema = `CREATE TABLE IF NOT EXISTS ${DATABASE_CONFIG.TABLES.FINISH_TIMES} (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        time INTEGER NOT NULL,
        created_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`;
      
    //  console.log('🔧 Creating finish_times table...');
      await this.db.execute(finishTimesSchema);
     // console.log(`✅ Table '${DATABASE_CONFIG.TABLES.FINISH_TIMES}' created/verified`);

      // Create barcodes table
      const barcodesSchema = `CREATE TABLE IF NOT EXISTS ${DATABASE_CONFIG.TABLES.BARCODES} (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        user INTEGER NOT NULL,
        position INTEGER NOT NULL,
        created_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`;
      
   //   console.log('🔧 Creating barcodes table...');
      await this.db.execute(barcodesSchema);
    //  console.log(`✅ Table '${DATABASE_CONFIG.TABLES.BARCODES}' created/verified`);

      // Save to store for web platform
      if (!this.config.isNative) {
       // console.log('💾 Saving to web store...');
        await this.sqlite.saveToStore(this.config.dbName);
        //console.log('✅ Saved to web store');
      }

     // console.log('✅ All tables created successfully');
    } catch (error) {
      console.error('❌ Error creating tables:', error);
      throw new Error(`Table creation failed: ${error}`);
    }
  }

  /**
   * Execute a deferred clearTimes if one was interrupted by a previous
   * session (flag persisted in Preferences / iOS UserDefaults).
   */
  private async executePendingClear(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: DatabaseService.CLEAR_TIMES_PENDING_KEY });
      if (value !== 'true') return;

      console.log('🔄 Executing deferred clearTimes from previous session...');
      await this.db!.run(`DELETE FROM ${DATABASE_CONFIG.TABLES.FINISH_TIMES}`, []);
      await Preferences.remove({ key: DatabaseService.CLEAR_TIMES_PENDING_KEY });
      console.log('✅ Deferred clearTimes executed');
    } catch (error) {
      // Non-fatal: flag stays set, will retry on next initializeDatabase().
      console.error('⚠️ executePendingClear failed:', error);
    }
  }

  /**
   * Add a finish time record
   */
  public async addTime(time: number): Promise<DatabaseResult<FinishTime>> {
    try {
      const ready = await this.isConnectionReady();
      if (!ready) {
        return { success: false, error: 'Database connection not ready' };
      }

      if (!Number.isInteger(time) || time <= 0) {
        return { success: false, error: 'Invalid time value' };
      }

      const insertQuery = `INSERT INTO ${DATABASE_CONFIG.TABLES.FINISH_TIMES} (time) VALUES (?)`;
      const result = await this.db!.run(insertQuery, [time]);

      // Save to store for web platform
      if (!this.config.isNative) {
        await this.sqlite.saveToStore(this.config.dbName);
      }

      const newRecord: FinishTime = {
        id: result.changes?.lastId,
        time
      };

      console.log('✅ Time record added:', newRecord);
      return {
        success: true,
        data: newRecord
      };

    } catch (error) {
      const errorMessage = `Failed to add time: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Add a barcode record
   */
  public async addBarcode(user: number, position: number): Promise<DatabaseResult<Barcode>> {
    try {
      if (!await this.isConnectionReady()) {
        return { success: false, error: 'Database connection not ready' };
      }

      if (!Number.isInteger(user) || !Number.isInteger(position)) {
        return { success: false, error: 'Invalid user or position values' };
      }

      const insertQuery = `INSERT INTO ${DATABASE_CONFIG.TABLES.BARCODES} (user, position) VALUES (?, ?)`;
      const result = await this.db!.run(insertQuery, [user, position]);

      // Save to store for web platform
      if (!this.config.isNative) {
        await this.sqlite.saveToStore(this.config.dbName);
      }

      const newRecord: Barcode = {
        id: result.changes?.lastId,
        user,
        position
      };

      console.log('✅ Barcode record added:', newRecord);
      return {
        success: true,
        data: newRecord
      };

    } catch (error) {
      const errorMessage = `Failed to add barcode: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get all finish times
   */
  public async getTimes(): Promise<DatabaseResult<FinishTime[]>> {
    try {
      if (!await this.isConnectionReady()) {
        return { success: false, error: 'Database connection not ready' };
      }

      const query = `SELECT id, time FROM ${DATABASE_CONFIG.TABLES.FINISH_TIMES} ORDER BY id ASC`;
      const result = await this.db!.query(query);

      console.log('📊 Retrieved finish times:', result.values?.length || 0);
      return {
        success: true,
        data: (result.values as FinishTime[]) || []
      };

    } catch (error) {
      const errorMessage = `Failed to get times: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get all barcodes
   */
  public async getBarcodes(): Promise<DatabaseResult<Barcode[]>> {
    try {
      if (!await this.isConnectionReady()) {
        return { success: false, error: 'Database connection not ready' };
      }

      const query = `SELECT id, user, position FROM ${DATABASE_CONFIG.TABLES.BARCODES} ORDER BY id ASC`;
      const result = await this.db!.query(query);

      console.log('📊 Retrieved barcodes:', result.values?.length || 0);
      return {
        success: true,
        data: (result.values as Barcode[]) || []
      };

    } catch (error) {
      const errorMessage = `Failed to get barcodes: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Clear all finish times
   */
  public async clearTimes(): Promise<DatabaseResult> {
    try {
      // Persist the intent BEFORE attempting the DELETE so that if the app
      // is killed mid-operation the flag survives in iOS UserDefaults and
      // initializeDatabase() will re-run the DELETE on the next cold start.
      await Preferences.set({ key: DatabaseService.CLEAR_TIMES_PENDING_KEY, value: 'true' });

      if (!await this.isConnectionReady()) {
        // Flag stays set — will be handled by next initializeDatabase().
        return { success: false, error: 'Database connection not ready' };
      }

      await this.db!.run(`DELETE FROM ${DATABASE_CONFIG.TABLES.FINISH_TIMES}`, []);

      // Save to store for web platform
      if (!this.config.isNative) {
        await this.sqlite.saveToStore(this.config.dbName);
      }

      // Delete succeeded — safe to clear the deferred flag.
      await Preferences.remove({ key: DatabaseService.CLEAR_TIMES_PENDING_KEY });

      console.log('🗑️ All finish times cleared');
      return { success: true };

    } catch (error) {
      // Flag stays set — will be handled by next initializeDatabase().
      const errorMessage = `Failed to clear times: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Clear all barcodes
   */
  public async clearBarcodes(): Promise<DatabaseResult> {
    try {
      if (!await this.isConnectionReady()) {
        return { success: false, error: 'Database connection not ready' };
      }

      const deleteQuery = `DELETE FROM ${DATABASE_CONFIG.TABLES.BARCODES}`;
      await this.db!.execute(deleteQuery);

      // Save to store for web platform
      if (!this.config.isNative) {
        await this.sqlite.saveToStore(this.config.dbName);
      }

      console.log('🗑️ All barcodes cleared');
      return { success: true };

    } catch (error) {
      const errorMessage = `Failed to clear barcodes: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Close database connection
   */
  public async closeDatabase(): Promise<DatabaseResult> {
    try {
      if (this.db) {
        await this.sqlite.closeConnection(this.config.dbName, false);
        this.db = null;
        
        this.initialised = false; // Legacy property
        this.isInitialized$.next(false);
        this.connectionStatus$.next('disconnected');
        
        console.log('✅ Database connection closed');
      }
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to close database: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Reset and reinitialize the database
   */
  public async resetDatabase(): Promise<DatabaseResult> {
    try {
      console.log('🔄 Resetting database...');
      
      // Close existing connection
      await this.closeDatabase();
      
      // Wait a bit
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Reinitialize
      return await this.initializeDatabase();
    } catch (error) {
      const errorMessage = `Failed to reset database: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Check if database connection is ready for operations
   */
  private async isConnectionReady(): Promise<boolean> {
    if (!this.db) {
      console.warn('⚠️ Database connection not available');
      return false;
    }

    if (!this.initialised) {
      console.warn('⚠️ Database not initialized');
      return false;
    }

    try {
      // Check if database is actually open
      const isOpen = await this.db.isDBOpen();
      if (!isOpen.result) {
        console.warn('⚠️ Database connection exists but is not open');
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('⚠️ Error checking database status:', error);
      return false;
    }
  }

  /**
   * Get current connection status
   */
  public getConnectionStatus(): 'disconnected' | 'connecting' | 'connected' {
    return this.connectionStatus$.getValue();
  }

  /**
   * Check if database is ready for operations
   */
  public isReady(): boolean {
    return this.initialised && this.connectionStatus$.getValue() == 'connected';
  }

  ngOnDestroy(): void {
    console.log('🧹 DatabaseService destroyed');
    
    this.destroy$.next();
    this.destroy$.complete();
    
    // Close database connection
    this.closeDatabase();
    
    // Complete observables
    this.isInitialized$.complete();
    this.connectionStatus$.complete();
    
    console.log('✅ DatabaseService cleanup complete');
  }
}
